import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import {
  CHAT_CHANNEL_TYPE,
  CHAT_MESSAGE_RATING,
  ChatMessage,
} from '@/types/chat';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts bytes to a human-readable file size string
 * @param bytes - The number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string (e.g., "1.5 MB", "2.3 GB")
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Converts bytes to MB
 * @param bytes - The number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns Number in MB
 */
export function bytesToMB(bytes: number, decimals: number = 2): number {
  return parseFloat((bytes / (1024 * 1024)).toFixed(decimals));
}

/**
 * Converts bytes to MB and returns as string
 * @param bytes - The number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns String in MB format (e.g., "1.5 MB")
 */
export function bytesToMBString(bytes: number, decimals: number = 2): string {
  const mb = bytesToMB(bytes, decimals);
  return `${mb} MB`;
}

export function randomId(): string {
  return Math.random().toString(36).substring(2, 15);
}

export function randomNumId(): number {
  return Math.floor(Math.random() * 1000000);
}

export function tryCatch<T, E = Error>(fn: () => T) {
  type Result<TResult, EResult> =
    | { data: TResult; error: null }
    | { data: null; error: EResult };
  type ReturnType =
    T extends Promise<infer P> ? Promise<Result<P, E>> : Result<T, E>;

  try {
    const result = fn();
    if (result instanceof Promise) {
      return result
        .then((data: Promise<unknown>) => ({ data, error: null }))
        .catch((e: unknown) => {
          return { data: null, error: e as E };
        }) as ReturnType;
    } else {
      return { data: result, error: null } as ReturnType;
    }
  } catch (e: unknown) {
    return { data: null, error: e as E } as ReturnType;
  }
}

/**
 * The Xano agent gateways send voice turns to FSI as
 * `[typed text\n\n][Voice note transcription]\n<transcript>`, and the FSI
 * voice socket sends hands-free turns as `[Voice transcription]\n<transcript>`.
 * FSI stores both verbatim. Removing the marker restores what the user saw live.
 */
const VOICE_NOTE_MARKER = /\[Voice (?:note )?transcription\]\s*/;

export function stripVoiceNoteMarker(value: string): string {
  return value.replace(VOICE_NOTE_MARKER, '');
}

export function createChatMessage({
  content,
  type,
  rating = null,
  id = randomId(),
  isStreaming = false,
  isLoader = false,
  timestamp,
  tools,
}: {
  content: string;
  type: CHAT_CHANNEL_TYPE;
  rating?: CHAT_MESSAGE_RATING | null;
  id?: string;
  isStreaming?: boolean;
  isLoader?: boolean;
  timestamp?: number;
  tools?: string[];
}): ChatMessage {
  const date = timestamp ? new Date(timestamp) : new Date();

  return {
    id,
    rating,
    type,
    content,
    tools,
    isStreaming,
    isLoader,
    timestamp: date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
  };
}
